import tailwindAnimate from 'tailwindcss-animate'
import twColors from 'tailwindcss/colors'
import plugin from 'tailwindcss/plugin'
import type { PluginAPI, Config } from 'tailwindcss/types/config'
import { BRAND_FONT_FEATURES, fontSize, typographyPlugin } from './src/styles/typography'

// Prism 12-step ramps from packages/ui/src/styles/tokens.color.css.
// Relative color syntax keeps opacity modifiers (bg-gray-9/50) working on oklch vars.
// black/white are alpha ramps, so they expose the var as-is: a modifier would replace the alpha.
const RAMP_STEPS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12] as const

// Relative colour syntax, not a bare `var()`: a bare var drops the opacity modifier silently, so
// `ring-danger/30` would paint at full strength. Safe only on a solid colour: doing the same to an
// alpha token would replace its alpha, which is why those stay bare vars below.
const oklchVar = (token: string) => `oklch(from var(--${token}) l c h / <alpha-value>)`

// One token and its suffixed siblings as a colour family: `family('link', ['hover'])` is `link` and
// `link-hover`. `root: false` for a family with no bare name of its own.
function family(base: string, keys: readonly string[], { root = true } = {}) {
  return {
    ...(root ? { DEFAULT: oklchVar(base) } : {}),
    ...Object.fromEntries(keys.map((key) => [key, oklchVar(`${base}-${key}`)])),
  }
}

function ramp(name: string, { alpha = false } = {}) {
  return Object.fromEntries(
    RAMP_STEPS.map((step) => [step, alpha ? `var(--${name}-${step})` : oklchVar(`${name}-${step}`)]),
  )
}

// Tone families from tokens.semantic.css. One shape per tone, which is what lets the button
// express `variant × tone` as a grid instead of eight hand-written variants: `primary` takes the
// solid and its two press states, `secondary` and `ghost` take the subtle fills, `outline` takes
// the border, and everything but a solid takes the text colour.
//
// Each part is registered on its own property, so the utility reads as the token does:
// `--danger-text` is `text-danger`, `--danger-border` is `border-danger`, `--danger` is `bg-danger`.
function tone(name: string, { border = true, ramp: rampName }: { border?: boolean; ramp?: string } = {}) {
  return {
    // The fills, and the default for any property not given its own part below: `bg-danger`, the
    // subtle `bg-danger-subtle`, and the ring and outline colour.
    colors: {
      // A family that shares its name with a ramp keeps the raw steps beside it: `bg-teal-9`.
      ...(rampName ? ramp(rampName) : {}),
      ...family(name, ['hover', 'active', 'subtle', 'subtle-hover', 'subtle-active']),
    },
    // `text-danger` is the step-12 text, `text-danger-subtle` step 11.
    text: family(`${name}-text`, ['subtle']),
    // An object, so it merges with the fills for `border-*` rather than replacing them.
    border: border ? { DEFAULT: oklchVar(`${name}-border`) } : {},
    // The text on the solid: `text-on-danger`. A bare var: these are black or white alpha steps.
    on: `var(--text-on-${name})`,
  }
}

// The fixed grounds, shaped like a tone. Bare vars: they are alpha steps, which an opacity modifier
// would replace.
function fixedGround(name: 'on-light' | 'on-dark') {
  return {
    colors: {
      DEFAULT: `var(--${name})`,
      hover: `var(--${name}-hover)`,
      active: `var(--${name}-active)`,
      subtle: `var(--${name}-subtle)`,
      'subtle-hover': `var(--${name}-subtle-hover)`,
      'subtle-active': `var(--${name}-subtle-active)`,
      // The edge as a fill, for a divider drawn as a box or an inset hairline: `bg-on-light-border`.
      border: `var(--${name}-border)`,
    },
    text: { DEFAULT: `var(--${name}-text)`, inverse: `var(--${name}-text-inverse)` },
    border: { DEFAULT: `var(--${name}-border)` },
  }
}

// Category tones carry no meaning and outline in nothing, so no border part. Each shares its name with
// its ramp, as brand does with the yellow it is cut from.
const CATEGORIES = ['yellow', 'teal', 'indigo', 'purple', 'pink', 'brown'] as const
const TONES = {
  brand: tone('brand', { ramp: 'brand' }),
  danger: tone('danger'),
  success: tone('success'),
  warning: tone('warning'),
  info: tone('info'),
  ...Object.fromEntries(CATEGORIES.map((name) => [name, tone(name, { border: false, ramp: name })])),
}
const FIXED = { 'on-light': fixedGround('on-light'), 'on-dark': fixedGround('on-dark') }
const toneParts = <K extends 'colors' | 'text' | 'border'>(part: K) =>
  Object.fromEntries([...Object.entries(TONES), ...Object.entries(FIXED)].map(([name, parts]) => [name, parts[part]]))
const onTones = Object.fromEntries(Object.entries(TONES).map(([name, parts]) => [`on-${name}`, parts.on]))

// --border is an alpha step (black-6 / white-6). Bare, the token is used as is. A modifier states the
// alpha outright: `border-border/20` is the border colour at 20%, not 20% of the token's own alpha, which
// all but vanishes. Tailwind hands a bare utility `var(--tw-*-opacity, 1)` and a modifier its value.
// Its runtime takes a function of the opacity here; its types only allow a string, hence the cast.
const borderColor = (({ opacityValue }: { opacityValue?: string }) =>
  opacityValue === undefined || opacityValue.startsWith('var(')
    ? 'var(--border)'
    : `oklch(from var(--border) l c h / ${opacityValue})`) as unknown as string

// The neutral edges, for any property that draws one: `border-strong`, `outline-strong`, `ring-strong`.
// Alpha steps, so bare vars. Tones stay out: `ring-danger` and `outline-danger` take the fill.
const edgeColors = {
  strong: 'var(--border-strong)',
  emphasis: 'var(--border-emphasis)',
  marker: 'var(--border-marker)',
  hover: 'var(--border-hover)',
  disabled: 'var(--border-disabled)',
}

// Experimental plugin for tailwind custom prefixes, e.g.:
// `assistant:bg-dark/80 app:bg-dark/40` for diff bg styles for diff clients
const clients = ['assistant', 'app', 'overlay'] as const
const clientVariant = plugin(function (this: void, api: PluginAPI) {
  const { addVariant, e } = api

  clients.forEach((client) => {
    // Positive
    // @ts-ignore
    addVariant(client, ({ modifySelectors, separator }) => {
      // @ts-ignore
      modifySelectors(
        ({ className }: { className: string }) =>
          `[data-client="${client}"] .${e(`${client}${separator}${className}`)}`,
      )
    })

    // Negative
    // @ts-ignore
    addVariant(`not-${client}`, ({ modifySelectors, separator }) => {
      // @ts-ignore
      modifySelectors(
        ({ className }: { className: string }) =>
          `[data-client]:not([data-client="${client}"]) .${e(`not-${client}${separator}${className}`)}`,
      )
    })
  })
})

// Electron-only squircle corners (`-electron-corner-smoothing`, Electron 34+).
// Applies wherever an element has a border-radius; other renderers ignore it.
// `corner-sharp` is emitted second so it overrides `corner-smooth` on the same
// element — pair it with `rounded-full`, where a squircle would distort a circle.
// Two properties for one effect. `-electron-corner-smoothing` is Chromium's Electron-only
// property and is all the app itself needs; `corner-shape` is the standard that does the same job
// in a plain browser, which is what the design system and the landing site render in. A browser
// that has neither ignores both and draws an ordinary rounded rectangle.
const cornerSmoothing = plugin(function (this: void, api: PluginAPI) {
  api.addUtilities({
    '.corner-smooth': { '-electron-corner-smoothing': 'system-ui', 'corner-shape': 'squircle' },
    // The standard squircle alone, for a half-height corner. Electron's smoothing eases into a
    // straight run of edge, and a half-height corner leaves none, so it is left off here.
    '.corner-squircle': { 'corner-shape': 'squircle' },
    // Sharp is for a radius already clamped to half the height. There a squircle replaces the
    // round ends rather than softening them, handing back the rounded rectangle the pill exists
    // to leave, so the corner has to be told to stay round.
    '.corner-sharp': { '-electron-corner-smoothing': '0%', 'corner-shape': 'round' },
  })
})

// The focus ring as a utility, for a component that wants it in a tone, or on an element `base.css`
// leaves out. The same outline the base rule draws, only the colour changes.
const focusRing = plugin(function (this: void, api: PluginAPI) {
  const ring = (color: string) => ({
    outline: `var(--focus-ring-width) solid ${color}`,
    'outline-offset': 'var(--focus-ring-offset)',
  })
  const toned = (color: string) => ring(`oklch(from var(${color}) l c h / var(--focus-ring-alpha))`)
  api.addUtilities({
    '.focus-ring': ring('var(--focus-ring)'),
    '.focus-ring-danger': toned('--danger-ring'),
    '.focus-ring-success': toned('--success-ring'),
    '.focus-ring-on-light': ring('var(--focus-ring-on-light-color)'),
    '.focus-ring-on-dark': ring('var(--focus-ring-on-dark-color)'),
  })
})

// Disabled either way: Base UI's `data-disabled`, or native `:disabled`, which is all a control gets
// from an enclosing `<fieldset disabled>`.
const disabledVariant = plugin(function (this: void, api: PluginAPI) {
  api.addVariant('is-disabled', '&:is(:disabled,[data-disabled])')
})

// Invalid either way: `aria-invalid` on a bare control, or Base UI's `data-invalid` on a control
// inside an invalid Field.
const invalidVariant = plugin(function (this: void, api: PluginAPI) {
  api.addVariant('is-invalid', '&:is([aria-invalid=true],[data-invalid])')
})

const platforms = ['win', 'mac'] as const
const platformVariant = plugin(function (this: void, api: PluginAPI) {
  const { addVariant, e } = api

  platforms.forEach((platform) => {
    // Positive
    // @ts-ignore
    addVariant(platform, ({ modifySelectors, separator }) => {
      // @ts-ignore
      modifySelectors(
        ({ className }: { className: string }) =>
          `[data-platform="${platform}"] .${e(`${platform}${separator}${className}`)}`,
      )
    })

    // Negative
    // @ts-ignore
    addVariant(`not-${platform}`, ({ modifySelectors, separator }) => {
      // @ts-ignore
      modifySelectors(
        ({ className }: { className: string }) =>
          `[data-platform]:not([data-platform="${platform}"]) .${e(`not-${platform}${separator}${className}`)}`,
      )
    })
  })
})

/** @type {import('tailwindcss').Config} */
export default {
  darkMode: ['class'],
  content: ['./src/**/*.{js,ts,jsx,tsx,mdx}'],
  theme: {
    // Outside `extend`, so Tailwind's own steps (`text-base`, `text-xl`…) are gone, not kept beside these.
    fontSize,
    /* Prism's shadow ramp, from tokens.shadow.css. Outside `extend` on purpose: it replaces
       Tailwind's set, so bare `shadow`, `shadow-2xl` and `shadow-inner` emit nothing. Each step
       carries its own 1px edge, so a shadowed element needs no border. */
    boxShadow: {
      none: '0 0 #0000',
      xs: 'var(--shadow-xs)',
      sm: 'var(--shadow-sm)',
      md: 'var(--shadow-md)',
      lg: 'var(--shadow-lg)',
      xl: 'var(--shadow-xl)',
    },
    extend: {
      // Not in Tailwind 3's default set, so without this every `aria-invalid:` class emits nothing.
      aria: { invalid: 'invalid="true"' },
      fontFamily: {
        sans: ['var(--font-inter)', 'sans-serif'],
        mono: ['var(--font-dm-mono)'],
        aeonik: ['Aeonik', 'sans-serif'],
        brand: [['"Greed Standard"', 'var(--font-inter)', 'sans-serif'], { fontFeatureSettings: BRAND_FONT_FEATURES }],
      },
      fontWeight: {
        350: '350',
        450: '450',
        normal: '400',
        medium: '500',
      },
      spacing: {
        '1.5em': '1.5em',
      },
      colors: {
        transparent: 'transparent',
        // One name per job: the page and the backgrounds on it, the fills of controls, the text in four steps.
        background: family('background', ['raised', 'subtle', 'overlay', 'inverse', 'selected']),
        // Alpha steps, so bare vars: an opacity modifier would replace their alpha.
        element: {
          DEFAULT: 'var(--element)',
          hover: 'var(--element-hover)',
          active: 'var(--element-active)',
          disabled: 'var(--element-disabled)',
        },
        foreground: family('foreground', ['secondary', 'placeholder', 'disabled']),
        border: borderColor,
        ...toneParts('colors'),
        light: 'hsl(var(--light))',
        dark: 'hsl(var(--dark))',
        gray: ramp('gray'),
        highlight: family('highlight', ['line']),
        selection: oklchVar('selection'),
        // Alpha, so a bare var: a modifier would replace it.
        skeleton: 'var(--skeleton)',
        backdrop: { DEFAULT: 'var(--backdrop)', subtle: 'var(--backdrop-subtle)' },
        link: family('link', ['hover', 'underline']),
        code: family('code', ['text', 'background'], { root: false }),
        // The focus ring, colour and strength both from the tokens, so `ring-focus` takes no
        // opacity modifier: the strength is set per theme there, not per call site.
        focus: 'var(--focus-ring)',
        orange: ramp('orange'),
        black: { DEFAULT: twColors.black, ...ramp('black', { alpha: true }) },
        white: { DEFAULT: twColors.white, ...ramp('white', { alpha: true }) },
        green: { DEFAULT: 'hsl(var(--green))', ...ramp('green') },
        'green-100': 'hsl(var(--green-100))',
        'green-200': 'hsl(var(--green-200))',
        'green-300': 'hsl(var(--green-300))',
        'green-400': 'hsl(var(--green-400))',
        'green-500': 'hsl(var(--green-500))',
        'green-600': 'hsl(var(--green-600))',
        'green-700': 'hsl(var(--green-700))',
        'green-800': 'hsl(var(--green-800))',
        'green-900': 'hsl(var(--green-900))',
        'green-950': 'hsl(var(--green-950))',
        red: { DEFAULT: 'hsl(var(--red))', ...ramp('red') },
        primary: 'hsl(var(--primary))',
        // DEFAULT only: a bare string here would replace Tailwind's whole blue
        // scale and break existing blue-500-style utilities.
        blue: { ...twColors.blue, DEFAULT: 'hsl(var(--blue))', ...ramp('blue') },
        'premium-green': 'hsl(var(--premium-green))',
        'premium-blue': 'hsl(var(--premium-blue))',
        'bg-layer-1': 'hsl(var(--bg-layer-1))',
        'light-2': 'hsla(var(--light-2))',
        'light-3': 'hsla(var(--light-3))',
        'light-4': 'hsla(var(--light-4))',
        'light-5': 'hsla(var(--light-5))',
        'light-8': 'hsla(var(--light-8))',
        'light-10': 'hsla(var(--light-10))',
        'light-15': 'hsla(var(--light-15))',
        'light-16': 'hsla(var(--light-16))',
        'light-20': 'hsla(var(--light-20))',
        'light-40': 'hsla(var(--light-40))',
        'light-50': 'hsla(var(--light-50))',
        'light-60': 'hsla(var(--light-60))',
        'light-70': 'hsla(var(--light-70))',
        'light-80': 'hsla(var(--light-80))',
        'light-90': 'hsla(var(--light-90))',
        'dark-5': 'hsla(var(--dark-5))',
        'dark-10': 'hsla(var(--dark-10))',
        'dark-20': 'hsla(var(--dark-20))',
        'dark-40': 'hsla(var(--dark-40))',
        'dark-60': 'hsla(var(--dark-60))',
        'dark-80': 'hsla(var(--dark-80))',
        'dark-90': 'hsla(var(--dark-90))',
        'green-20': 'hsla(var(--green-20))',
        'green-40': 'hsla(var(--green-40))',
        'green-60': 'hsla(var(--green-60))',
        'green-80': 'hsla(var(--green-80))',
        'red-20': 'hsla(var(--red-20))',
        'red-40': 'hsla(var(--red-40))',
        'red-60': 'hsla(var(--red-60))',
        'red-80': 'hsla(var(--red-80))',
        'primary-5': 'hsla(var(--primary-5))',
        'primary-10': 'hsla(var(--primary-10))',
        'primary-20': 'hsla(var(--primary-20))',
        'primary-30': 'hsla(var(--primary-30))',
        'primary-40': 'hsla(var(--primary-40))',
        'primary-50': 'hsla(var(--primary-50))',
        'primary-60': 'hsla(var(--primary-60))',
        'primary-70': 'hsla(var(--primary-70))',
        'primary-80': 'hsla(var(--primary-80))',
        'primary-90': 'hsla(var(--primary-90))',
        'purple-60': 'hsla(var(--purple-60))',
        'purple-80': 'hsla(var(--purple-80))',
        'yellow-80': 'hsla(var(--yellow-80))',
        'warning-5': 'hsla(var(--warning-5))',
        'warning-10': 'hsla(var(--warning-10))',
        'warning-20': 'hsla(var(--warning-20))',
        'warning-40': 'hsla(var(--warning-40))',
        'warning-60': 'hsla(var(--warning-60))',
        'warning-80': 'hsla(var(--warning-80))',
      },
      textColor: {
        primary: 'hsl(var(--text-primary))',
        'secondary-old': 'hsl(var(--text-secondary-old))',
        tertiary: 'hsl(var(--text-tertiary))',
        // A tone's text colour, by property: `text-danger` is --danger-text, not the solid.
        ...toneParts('text'),
        indicator: 'var(--indicator)',
        ...onTones,
        'on-inverse': oklchVar('text-on-inverse'),
        // Alpha, so a bare var.
        'on-inverse-secondary': 'var(--text-on-inverse-secondary)',
        'on-selected': oklchVar('text-on-selected'),
        // An object, not a string: a string here would hide the ramp steps for text-* utilities.
        black: { DEFAULT: 'hsl(var(--text-black))', ...ramp('black', { alpha: true }) },
      },
      backgroundColor: {
        primary: 'hsl(var(--bg-primary))',
        'secondary-old': 'hsl(var(--bg-secondary-old))',
        hover: 'hsl(var(--bg-hover))',
        tertiary: 'hsl(var(--bg-tertiary))',
      },
      // A bare `border` (and `border-b`, `divide-y`, …) is the hairline, `--border-hairline`. `border-1` is
      // the full pixel Tailwind's `border` used to be. Numeric, not a word: tailwind-merge reads an unknown
      // `border-*` word as a colour and would drop the element's border colour.
      borderWidth: {
        DEFAULT: 'var(--border-hairline)',
        '1': '1px',
      },
      borderColor: {
        // A bare `border` takes the default border colour, not Tailwind's fixed #e5e7eb, which stays light in dark.
        DEFAULT: 'var(--border)',
        ...edgeColors,
        // A tone's edge, by property: `border-danger` is --danger-border, not the solid.
        ...toneParts('border'),
        primary: 'hsl(var(--border-primary))',
        'secondary-old': 'hsl(var(--border-secondary-old))',
        tertiary: 'hsl(var(--border-tertiary))',
      },
      // No DEFAULT on either: a bare `outline` is the outline style, and a bare `ring` keeps Tailwind's colour.
      outlineColor: edgeColors,
      ringColor: edgeColors,
      keyframes: {
        'chip-scan': {
          '0%': { transform: 'translateX(-100%)' },
          '100%': { transform: 'translateX(300%)' },
        },
      },
      animation: {
        'chip-scan': 'chip-scan 2.5s ease-in-out infinite',
      },
      /* The shape scale, from tokens.shape.css, mapped by role rather than by name: the scale's
         `sm` is for "inputs, buttons, tags", which is the job `rounded-lg` already does here.

         The old entries read `calc(var(--radius, …) - 4px)` against a `--radius` that is defined
         nowhere, so `rounded-sm` resolved to -2px and rendered square. */
      borderRadius: {
        sm: 'var(--radius-control)',
        DEFAULT: 'var(--radius-control)',
        md: 'var(--radius-control)',
        lg: 'var(--radius-sm)',
        xl: 'var(--radius-md)',
        '2xl': 'var(--radius-lg)',
        '3xl': 'var(--radius-xl)',
        full: 'var(--radius-pill)',
      },
    },
  },
  plugins: [
    tailwindAnimate,
    clientVariant,
    platformVariant,
    cornerSmoothing,
    focusRing,
    disabledVariant,
    invalidVariant,
    typographyPlugin,
  ],
  safelist: ['text-warning-60', 'text-warning-80', 'bg-warning-60', 'bg-warning-80'],
  variants: {
    extend: {
      display: ['group-hover'],
    },
  },
} satisfies Config
